import React, { useRef, useEffect, useState } from 'react';
import { Box } from '@material-ui/core';
import NavigationArrow from './NavigationArrow';
import ToolCard from './ToolCard';
import { useStyles } from './styles';

const CardContainer: React.FC = () => {
  const styles = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const toolsData = [
    {
      id: 9,
      category: 'Analytics',
      title: 'GitHub CoPilot Cost Center',
      description:
        'Monitor AIC consumption and GitHub Copilot charges by cost center.',
      type: 'logo' as const,
      image: '/images/home/cost_center_banner.png',
      line: {
        initial: '77px',
        hover: '50px',
      },
      link: '/github-cost-center',
    },
    {
      id: 8,
      category: 'Design',
      title: 'Design Lint',
      description:
        'Automatically validate your Figma designs against CBRE brand guidelines, accessibility standards (WCAG 2.1), and best practices. Get instant feedback with actionable fixes for colors, typography, spacing, naming conventions, and more.',
      type: 'logo' as const,
      image: '/images/home/design_lint_banner.png',
      line: {
        initial: '77px',
        hover: '50px',
      },
      link: '/ai-in-sdlc#Design',
    },
    {
      id: 1,
      category: 'Quality Engineering',
      title: 'QE Score Agent',
      description:
        'AI-powered QE score monitoring that identifies root causes and delivers targeted corrective actions to enhance quality.',
      type: 'logo' as const,
      image: '/images/home/QE Score Agent.png',
      line: {
        initial: '36px',
        hover: '69px',
      },
      link: '/ai-in-sdlc#Quality%20Engineering',
    },
    {
      id: 7,
      category: 'Plan',
      title: 'Planner Agent',
      description:
        'The Gen AI-powered Planner Agent is an all-in-one plugin for ADO and Jira that enhances product planning process.',
      type: 'logo' as const,
      image: '/images/home/Planner-Agent.png',
      line: {
        initial: '36px',
        hover: '69px',
      },
      link: '/ai-in-sdlc#Plan%20&%20Analyze',
    },
    {
      id: 3,
      category: 'Design',
      title: 'Designer Agent',
      description:
        'Designer Agent turns your Figma designs into clean, responsive code with AI. Drag, drop, design - it writes the website for you!',
      type: 'logo' as const,
      image: '/images/home/Builderio.png',
      line: {
        initial: '77px',
        hover: '50px',
      },
      link: '/ai-in-sdlc#Design',
    },
    {
      id: 2,
      category: 'Build & Maintain',
      title: 'Gen AI PR Agent',
      description:
        'A gen AI assisted pull request uses AI to automatically review code changes, suggest improvements, and provide context.',
      type: 'logo' as const,
      image: '/images/home/GenAI-PRbot.png',
      line: {
        initial: '85px',
        hover: '120px',
      },
      link: '/ai-in-sdlc#Build%20&%20Maintain',
    },
    {
      id: 4,
      category: 'Build & Maintain',
      title: 'Github Copilot',
      description:
        'GitHub Copilot is an AI-powered code completion tool that suggests code snippets as you type, based on your code and comments.',
      type: 'logo' as const,
      image: '/images/home/Github.png',
      line: {
        initial: '97px',
        hover: '120px',
      },
      link: '/ai-in-sdlc#Build%20&%20Maintain',
    },
    {
      id: 6,
      category: 'Quality Engineering',
      title: 'QE Agent',
      description:
        'The Gen AI-powered QE Agent is an all-in-one plugin for ADO and Jira that enhances QA process at CBRE.',
      type: 'logo' as const,
      image: '/images/home/QE-Agent.png',
      line: {
        initial: '36px',
        hover: '69px',
      },
      link: '/ai-in-sdlc#Quality%20Engineering',
    },
    {
      id: 5,
      category: 'AI',
      title: 'Prompt Enhancer',
      description:
        'The enhancer is targeted for D&T community and will help with Software related inputs. The output will add context and templatize the prompt so that you can re-use it again as needed and share it with others.',
      type: 'logo' as const,
      image: '/images/home/Prompt-enhancer.png',
      line: {
        initial: '57px',
        hover: '120px',
      },
      link: '/prompt-store/prompt-enhancer',
    },
  ];

  const handlePrevClick = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const handleNextClick = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (containerRef.current) {
        containerRef.current.scrollLeft += e.deltaY;
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel);
    }

    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel);
      }
    };
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (containerRef.current) {
      setIsDragging(true);
      setStartX(e.pageX - containerRef.current.offsetLeft);
      setScrollLeft(containerRef.current.scrollLeft);
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !containerRef.current) return;
    e.preventDefault();
    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = x - startX;
    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  return (
    <section className={styles.container}>
      <header className={styles.header}>
        <h2 className={styles.showCaseTitle}>
          We've got excited updates for you
        </h2>
        <div className={styles.navigationControls}>
          <NavigationArrow direction="left" onClick={handlePrevClick} />
          <NavigationArrow direction="right" onClick={handleNextClick} />
        </div>
      </header>
      {/* @ts-ignore */}
      <Box
        className={styles.cardsContainer}
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        {toolsData.map(tool => (
          <ToolCard
            key={tool.id}
            category={tool.category}
            title={tool.title}
            description={tool.description}
            type={tool.type}
            image={tool.image}
            icon={tool.icon}
            customContent={tool.customContent}
            line={tool.line}
            link={tool.link}
          />
        ))}
      </Box>
    </section>
  );
};

export default CardContainer;
